import { z } from "zod";
import { Id } from "./ids.js";

export const MEMORY_ZONE_NAME_MAX_LENGTH = 60;
export const MemoryZoneNameSchema = z.string().trim().min(1).max(MEMORY_ZONE_NAME_MAX_LENGTH);

/**
 * What a zone holds: the company's facts (one per Space), a department's memory, which its bots
 * share, or a project's, which chosen bots read.
 */
export const MEMORY_ZONE_KINDS = ["company", "department", "project"] as const;
export const MemoryZoneKindSchema = z.enum(MEMORY_ZONE_KINDS);
export type MemoryZoneKind = z.infer<typeof MemoryZoneKindSchema>;
/** The kinds an admin can pick; the Company zone is made by setup. */
export const EditableMemoryZoneKindSchema = z.enum(["department", "project"]);

/** Bots in a Space that share memory with each other and nobody else. */
export const MemoryZoneSchema = z.object({
  id: Id,
  name: z.string(),
  /** The Space's Company zone: every bot reads it, and it cannot be removed. */
  company: z.boolean().optional(),
  kind: MemoryZoneKindSchema.default("department"),
  botIds: z.array(Id),
  /** A project zone's readers: the bots it is shared with. */
  sharedBotIds: z.array(Id).default([]),
});
export type MemoryZone = z.infer<typeof MemoryZoneSchema>;

/**
 * A Space's zones and the bots that can be placed in them: every bot in the Space for an admin,
 * the bots they can see for anyone else. `canManage` is whether the caller may change zones.
 */
export const MemoryZonesViewSchema = z.object({
  canManage: z.boolean(),
  zones: z.array(MemoryZoneSchema),
  bots: z.array(z.object({ id: Id, name: z.string(), memoryZoneId: Id.nullable() })),
});
export type MemoryZonesView = z.infer<typeof MemoryZonesViewSchema>;

/** Memory a bot may read but never write: other zones and other bots' own memory. */
export const BotMemoryGrantsSchema = z.object({
  botId: Id,
  zoneIds: z.array(Id),
  botIds: z.array(Id),
});
export type BotMemoryGrants = z.infer<typeof BotMemoryGrantsSchema>;

export const MEMORY_GRANTS_MAX = 50;

/**
 * What a bot may do in a zone. View reads its notes, Write changes them and Delete removes them;
 * Delete needs Write. A bot always views and writes its own zone (`home`), and views the Company
 * zone.
 */
export const ZoneAccessSchema = z.object({
  view: z.boolean(),
  write: z.boolean(),
  delete: z.boolean(),
});
export type ZoneAccess = z.infer<typeof ZoneAccessSchema>;

export const ZoneAccessEntrySchema = ZoneAccessSchema.extend({
  botId: Id,
  /** The bot's own zone. */
  home: z.boolean(),
});
export type ZoneAccessEntry = z.infer<typeof ZoneAccessEntrySchema>;

export const ZoneAccessViewSchema = z.object({
  zoneId: Id,
  canManage: z.boolean(),
  entries: z.array(ZoneAccessEntrySchema),
});
export type ZoneAccessView = z.infer<typeof ZoneAccessViewSchema>;

export const SetZoneAccessInputSchema = ZoneAccessSchema.extend({ zoneId: Id, botId: Id });

/** Where a fact lives: a line of a memory note, or an auto-saved item. */
export const FactRefSchema = z.union([
  z.object({ documentId: Id, line: z.string().min(1).max(4_000) }),
  z.object({ autoItemId: Id }),
]);
export type FactRef = z.infer<typeof FactRefSchema>;

/** One fact as the semantic memory viewer shows it, with when it held and where it came from. */
export const MemoryFactSchema = z.object({
  id: z.string(),
  ref: FactRefSchema,
  text: z.string(),
  /** The date it held or was saved, as an ISO date. */
  date: z.string().nullable(),
  /** The teammate whose chat it was saved from. */
  savedBy: z.string().nullable(),
  /** Who set it, with their role when known. */
  setBy: z.string().nullable(),
  /** Where an approved fact came from ("document, policy.pdf"). */
  source: z.string().nullable(),
  canEdit: z.boolean(),
  canDelete: z.boolean(),
});
export type MemoryFact = z.infer<typeof MemoryFactSchema>;

export const ZoneFactsSchema = z.object({
  zone: z.object({ id: Id, name: z.string(), kind: MemoryZoneKindSchema }),
  /** The zone's About note. */
  purpose: z.string(),
  access: ZoneAccessSchema.extend({ home: z.boolean() }),
  facts: z.array(MemoryFactSchema),
});
export type ZoneFacts = z.infer<typeof ZoneFactsSchema>;

/** A bot's working memory: its own facts (Private) and each zone it reaches (Shared). */
export const BotFactsViewSchema = z.object({
  botId: Id,
  /** Space admins change zone access. */
  canManage: z.boolean(),
  private: z.array(MemoryFactSchema),
  shared: z.array(ZoneFactsSchema),
});
export type BotFactsView = z.infer<typeof BotFactsViewSchema>;
